import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '@c2fo/liquidity';
import { apiClient } from '../../../lib/apiClient';
import type { EmailSubscriptionSettings } from '../types';

interface SaveEmailSubscriptionRequest {
  userAuthUuid: string;
  settings: EmailSubscriptionSettings;
}

/**
 * Mutation hook for saving user email subscription settings
 *
 * Saves email preferences to the User API.
 *
 * @returns TanStack Query mutation
 */
export const useSaveEmailSubscriptionMutation = () => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userAuthUuid, settings }: SaveEmailSubscriptionRequest) => {
      return await apiClient()
        .post(`api/users/${userAuthUuid}/settings/email`, {
          json: settings,
        })
        .json<EmailSubscriptionSettings>();
    },
    onSuccess: () => {
      showSnackbar({
        message: 'Email preferences saved successfully',
      });
      // Invalidate user queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['emailSubscription'] });
    },
    onError: (error: Error) => {
      showSnackbar({
        message: `Failed to save email preferences: ${error.message}`,
      });
    },
  });
};
